import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import StarIcon from '@mui/icons-material/Star';
import { Dialog, IconButton, Typography, useMediaQuery } from '@mui/material';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { TwContainer } from '../../typings/tw.types';
import { imageUrl } from '../../utils';
import SubInfo from './SubInfo';

interface ClientCardProps extends TwContainer {
  name: string;
  icon: string;
  description: string;
  role: string;
  linkedIn?: string;
  onReadMoreChange?: (isOpen: boolean) => void;
}

const ClientCard = ({ name, icon, description, containerClasses, role, linkedIn, onReadMoreChange }: ClientCardProps) => {
  const isLessThan640 = useMediaQuery('(max-width: 640px)');
  const isLessThan1280 = useMediaQuery('(max-width: 1280px)');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const quoteRef = useRef<HTMLElement>(null);

  // The link only appears when the quote is actually taller than the clamp.
  useEffect(() => {
    const measure = () => {
      const quote = quoteRef.current;
      if (!quote) return;
      setIsOverflowing(quote.scrollHeight > quote.clientHeight + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [description, isLessThan1280]);

  const stars = (
    <div className="flex gap-0.5 text-yellow" aria-label="Rated 5 out of 5">
      {[0, 1, 2, 3, 4].map((star) => (
        <StarIcon key={star} style={{ fontSize: 20 }} />
      ))}
    </div>
  );

  const logo = (
    <img
      className={classNames('ml-auto w-auto max-w-[120px] shrink-0 object-contain object-right', {
        'h-8': isLessThan640,
        'h-10': !isLessThan640,
      })}
      src={imageUrl(icon)}
      alt=""
      loading="lazy"
    />
  );

  const openDialog = () => {
    setIsDialogOpen(true);
    onReadMoreChange?.(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    onReadMoreChange?.(false);
  };

  const linkedInLink = linkedIn && (
    <a
      href={linkedIn}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`View ${name} on LinkedIn`}
      className="shrink-0 text-light-black/50 transition-colors hover:text-light-black">
      <LinkedInIcon style={{ fontSize: 20 }} />
    </a>
  );

  const footer = (
    <div className="mt-6 flex items-center gap-4 border-t border-light-black/10 pt-5">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <SubInfo
            typographyStyles={{
              fontSize: isLessThan1280 ? 17 : 19,
              color: '#263238',
              fontWeight: 600,
              fontFamily: 'var(--fontBase)',
            }}>
            {name}
          </SubInfo>
          {linkedInLink}
        </div>
        <div className="text-sm text-light-black/70">{role}</div>
      </div>
      {logo}
    </div>
  );

  return (
    <>
      <div
        className={classNames(
          'relative flex h-full flex-col overflow-hidden rounded-[14px] border border-light-black/10 bg-white mx-0 md:mx-5 p-5 min-h-[450px] lg:p-8 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl',
          containerClasses,
        )}>
        <div className="absolute inset-x-0 top-0 h-1.5 bg-yellow" />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-5 top-4 select-none font-serif text-[110px] leading-none text-yellow/30">
          &ldquo;
        </span>

        <div className="relative flex flex-1 flex-col">
          <div className="mt-2">{stars}</div>
          <div className="mt-5 flex-1">
            <Typography
              ref={quoteRef}
              variant="subinformation"
              className="line-clamp-[8]"
              style={{
                fontFamily: 'var(--fontPrimary)',
                fontSize: isLessThan1280 ? 16 : 18,
                lineHeight: 1.6,
              }}>
              {description}
            </Typography>
            {isOverflowing && (
              <button
                type="button"
                onClick={openDialog}
                className="mt-2 text-sm font-semibold text-light-black underline decoration-yellow decoration-2 underline-offset-4 hover:decoration-light-black">
                Read more
              </button>
            )}
          </div>

          {footer}
        </div>
      </div>

      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        aria-label={`Testimonial from ${name}`}
        PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}>
        <div className="h-1.5 bg-yellow" />
        <div className="relative p-6 sm:p-8">
          <IconButton
            aria-label="Close testimonial"
            onClick={closeDialog}
            sx={{ position: 'absolute', top: 12, right: 12, color: '#263238' }}>
            <CloseIcon style={{ fontSize: 20 }} />
          </IconButton>
          <div className="mt-2 mr-10">{stars}</div>
          <Typography
            variant="subinformation"
            className="mt-5 block"
            style={{
              fontFamily: 'var(--fontPrimary)',
              fontSize: isLessThan1280 ? 16 : 18,
              lineHeight: 1.6,
            }}>
            {description}
          </Typography>
          {footer}
        </div>
      </Dialog>
    </>
  );
};

export default ClientCard;
